import { Component, ElementRef, OnInit, computed, input, output, resource, viewChild } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { CoreFacade } from 'app/core/core.facade';
import { AuthFacade } from 'app/auth/auth.facade';
import { ButtonModule } from 'primeng/button';
import { firstValueFrom, skipWhile } from 'rxjs';
import { SharedRoutes } from '@shared/shared.routes';
import { Menubar } from 'primeng/menubar';
import { RouterLink } from '@angular/router';
import { Menu } from 'primeng/menu';
import { DataTestIdPipe } from '../../../shared/pipes/data-test-id.pipe';
import { MenuDesignTokens } from '@primeuix/themes/types/menu';
import { DynamicDialogUtils } from '@shared/utils/dynamic-dialog-utils';
import { TimezoneComponent } from './timezone/timezone.component';
import { TimezoneAppItemOutputDto } from '@shared/api/model/timezone-app-item-output-dto';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { OverlayBadgeDesignTokens } from '@primeuix/themes/types/overlaybadge';
import { NotificationsPopoverComponent } from "app/notifications/containers/notifications-popover/notifications-popover.component";

@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.scss'],
    imports: [Menubar, RouterLink, Menu, ButtonModule, DataTestIdPipe, OverlayBadgeModule, NotificationsPopoverComponent]
})
export class NavbarComponent implements OnInit {
  readonly notificationPopoverCmp = viewChild<NotificationsPopoverComponent>("notificationPopover");

  readonly mobileItems = input<MenuItem[]>([{}]);
  
  readonly userItems = input.required<MenuItem[]>();

  readonly timezone = input<TimezoneAppItemOutputDto>();

  timezoneSet = output<void>();

  readonly currentUserResource = resource({
    loader: () => firstValueFrom(this.facade.user$.pipe(skipWhile(x => !x?.name))),
  });

  readonly currentUserName = computed<string>(() => {
    if (!this.currentUserResource.hasValue()) {
      return "";
    }

    return this.currentUserResource.value().name;
  });

  readonly currentUserItems = computed<MenuItem[]>(() => {
    let items = this.userItems().slice();
    const userName = this.currentUserName();

    items.push(
      {
        id: "1",
        label: userName,
        styleClass: "md:hidden"
      }
    );

    const logoutItem = items.find(x => x.id == "199");
    if (logoutItem){
      logoutItem.command = () => {
        this.logOut();
      };
    }

    const documentationItem = items.find(x => x.id == "4");
    if (documentationItem) {
      documentationItem.url = this.documentationUrl!;
    }

    const notificationsItem = items.find(x => x.id == "30");
    if (notificationsItem) {
      notificationsItem.command =  event => {
        this.openNotificationsFromUserMenu(event.originalEvent);
      };
    }

    items.push(
      {
        id: "190",
        label: $localize `Timezone`,
        command: () => this.openTimezoneDialog(),
      }
    );

    return items.sort((x, y) => Number(x.id) - Number(y.id));
  });

  currentItems: MenuItem[] = [{}];
  userMenuMobile: MenuItem[] = [];

  homeRouterLink = SharedRoutes.homeRoute.route;

  documentationUrl = "";

  navbarMenu: MenuDesignTokens = {
    root: {
      background: "var(--dark-gray)",
      borderColor: "rgba(0, 0, 0, 0.15)",
    },
    item: {
        color: "var(--text-color-secondary)",
        focusBackground: "var(--darker-gray)",
        focusColor: "var(--text-color-secondary)",
        borderRadius: "0",
    }
  };

  overlayBadgeDt: OverlayBadgeDesignTokens = {
    root: {
      outline: {
        color: "var(--text-color-secondary)",
      }
    },
  }

  constructor(
    private facade: CoreFacade,
    private authFacade: AuthFacade,
    private dynamicDialogUtils: DynamicDialogUtils,
    private elementRef: ElementRef) {

  }

  ngOnInit(): void {
    this.facade.appConfig$.subscribe(config => this.documentationUrl = config?.documentationUrl!);
  }

  logOut() {
    this.authFacade.logout();
  }

  openTimezoneDialog() {
    var ref = this.dynamicDialogUtils.open(
      this.elementRef,
      TimezoneComponent,
      {
        header: $localize `Select timezone`,
        width: "500px",
        data: {
          timezone: this.timezone()
        }
      }
    );

    ref.onClose.subscribe(saved => {
      if (saved) {
        this.timezoneSet.emit();
      }
    });
  }

  openNotificationsFromUserMenu(event: any) {
    this.notificationPopoverCmp()?.notificationPopup()?.toggle(event);
  }
}
